import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import axios from "axios";
import React, { useState } from "react";
import { toast } from "react-toastify";

const API_BASE_URL = process.env.REACT_APP_API_URL;

const EXCHANGE_RATE_INR_TO_CNY = 0.083;

const OrderManagement = ({ orders, setOrders }) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm")); // < 600px
  const [loading, setLoading] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const selectedLanguage = localStorage.getItem("selectedLanguage") || "en";

  const orderStatuses = [
    "Pending",
    "Processing",
    "Shipped",
    "Delivered",
    "Cancelled",
  ];
  const paymentStatuses = ["Pending", "Paid", "Failed"];

  // Filter orders based on search query
  const filteredOrders = orders.filter((order) => {
    const query = searchQuery.toLowerCase();
    const orderIdMatch = order._id.toLowerCase().includes(query);
    const customerNameMatch = order.userId?.name?.toLowerCase().includes(query);
    const emailMatch = order.userId?.email?.toLowerCase().includes(query);

    return orderIdMatch || customerNameMatch || emailMatch;
  });

  // Handle order status change
  const handleStatusChange = async (orderId, newStatus) => {
    const confirmUpdate = window.confirm(
      `Are you sure you want to update the order status to "${newStatus}"?`
    );
    if (!confirmUpdate) return;

    setLoading((prev) => ({ ...prev, [orderId]: true }));
    try {
      await axios.patch(`${API_BASE_URL}/admin/orders/${orderId}`, {
        orderStatus: newStatus,
      });
      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order._id === orderId ? { ...order, orderStatus: newStatus } : order
        )
      );
      toast.success("Order status updated successfully");
    } catch (error) {
      console.error("Error updating order status:", error);
      toast.error("Failed to update order status");
    } finally {
      setLoading((prev) => ({ ...prev, [orderId]: false }));
    }
  };

  // Handle payment status change
  const handlePaymentStatusChange = async (orderId, newPaymentStatus) => {
    const confirmUpdate = window.confirm(
      `Are you sure you want to update the payment status to "${newPaymentStatus}"?`
    );
    if (!confirmUpdate) return;

    setLoading((prev) => ({ ...prev, [orderId]: true }));
    try {
      await axios.patch(`${API_BASE_URL}/admin/orders/${orderId}`, {
        paymentStatus: newPaymentStatus,
      });
      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order._id === orderId
            ? { ...order, paymentStatus: newPaymentStatus }
            : order
        )
      );
      toast.success("Payment status updated successfully");
    } catch (error) {
      console.error("Error updating payment status:", error);
      toast.error("Failed to update payment status");
    } finally {
      setLoading((prev) => ({ ...prev, [orderId]: false }));
    }
  };

  const getPriceDetails = (amount) => {
    const isChinese = selectedLanguage === "zh-TW";
    const currencySymbol = isChinese ? "¥" : "₹";
    const conversionRate = isChinese ? EXCHANGE_RATE_INR_TO_CNY : 1;
    return {
      symbol: currencySymbol,
      convertedAmount: (amount * conversionRate).toFixed(2),
    };
  };

  // Clear search input
  const handleClearSearch = () => {
    setSearchQuery("");
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: { xs: 1, sm: 2, md: 3 },
        borderRadius: 2,
        maxWidth: "100%",
        overflowX: "auto",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { sm: "center" },
          mb: { xs: 2, sm: 3 },
          gap: { xs: 2, sm: 0 },
        }}
      >
        <Typography
          variant={isXs ? "h6" : "h5"}
          sx={{ fontSize: { xs: "1rem", sm: "1.25rem", md: "1.5rem" }, }}
          style={{fontWeight: "bold"}}
        >
          Order Management
        </Typography>
        <TextField
          size="small"
          placeholder="Search by ID, name, or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            width: {
              xs: "100%",
              sm: "250px",
              md: "350px",
              lg: "450px",
            },
            "& .MuiInputBase-root": {
              borderRadius: 1,
              backgroundColor: theme.palette.grey[100],
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={handleClearSearch}
                  aria-label="clear search"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <TableContainer>
        <Table sx={{ minWidth: { xs: 300, sm: 650 } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                Order ID
              </TableCell>
              {!isXs && (
                <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                  Customer
                </TableCell>
              )}
              <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                Total
              </TableCell>
              <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                Order Status
              </TableCell>
              <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                Payment Status
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredOrders.map((order) => {
              const { symbol, convertedAmount } = getPriceDetails(
                order.totalAmount
              );
              return (
                <TableRow key={order._id} hover>
                  <TableCell
                    sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                  >
                    {isXs ? order._id.slice(-4) : order._id.slice(-6)}
                  </TableCell>
                  {!isXs && (
                    <TableCell>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontSize: { sm: "0.8rem", md: "0.875rem" } }}
                        >
                          {order.userId?.name || "N/A"}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          sx={{ fontSize: { sm: "0.7rem", md: "0.75rem" } }}
                        >
                          {order.userId?.email || "N/A"}
                        </Typography>
                      </Box>
                    </TableCell>
                  )}
                  <TableCell
                    sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                  >
                    {symbol}
                    {convertedAmount}
                  </TableCell>
                  <TableCell>
                    <Select
                      value={order.orderStatus}
                      size="small"
                      onChange={(e) =>
                        handleStatusChange(order._id, e.target.value)
                      }
                      disabled={loading[order._id]}
                      sx={{
                        minWidth: { xs: 90, sm: 110, md: 120 },
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      }}
                    >
                      {orderStatuses.map((status) => (
                        <MenuItem
                          key={status}
                          value={status.toLowerCase()}
                          sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                        >
                          {status}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Select
                      value={order.paymentStatus}
                      size="small"
                      onChange={(e) =>
                        handlePaymentStatusChange(order._id, e.target.value)
                      }
                      disabled={
                        loading[order._id] || order.paymentMethod !== "cod"
                      }
                      sx={{
                        minWidth: { xs: 90, sm: 110, md: 120 },
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      }}
                    >
                      {paymentStatuses.map((status) => (
                        <MenuItem
                          key={status}
                          value={status.toLowerCase()}
                          sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                        >
                          {status}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                </TableRow>
              );
            })}
            {filteredOrders.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={isXs ? 4 : 5}
                  align="center"
                  sx={{ py: 3, fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                >
                  No orders found matching your search
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default OrderManagement;
